import { fitTitle, fitDescription } from "@/lib/seo-text";
import { Link, createFileRoute } from "@tanstack/react-router";

import { ExecutorIntakeForm } from "@/components/executor-intake-form";
import { CtaBand, PageHero } from "@/components/page-parts";
import { FIRM } from "@/lib/firm";

export const Route = createFileRoute("/executor-intake")({
  head: () => ({
    meta: [
      { title: fitTitle("Executor Intake — Start a Queensland Estate Online") },
      {
        name: "description",
        content:
          fitDescription("Executors: start a Queensland estate online. Tell us about the will, the assets and the safe custody of the original, and we'll confirm whether probate is needed and the fixed fee."),
      },
      { property: "og:title", content: fitTitle(`Executor intake | ${FIRM.name}`) },
      {
        property: "og:description",
        content:
          fitDescription("A short online intake for executors and next of kin — we confirm whether a grant is needed and quote a fixed fee in writing."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/executor-intake" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "/executor-intake" }],
  }),
  component: ExecutorIntakePage,
});

function ExecutorIntakePage() {
  return (
    <>
      <PageHero
        eyebrow="Executors & next of kin"
        title="Start the estate online"
        lead="Tell us what you know about the will and the assets. We'll confirm whether a grant of probate is actually required, quote a fixed fee in writing, and list exactly what we need from you next."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            { t: "Same or next business day", d: "You get a written answer on whether a grant is needed" },
            { t: "Fixed fee, in writing", d: "$3,300 incl GST for the grant, disbursements at cost" },
            { t: "Nothing to attend", d: "Video identity check, electronic filing, will by post" },
          ].map((c) => (
            <div key={c.t} className="rounded-sm border border-border bg-card p-6">
              <p className="font-serif text-lg text-primary">{c.t}</p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-12">
          <ExecutorIntakeForm />
        </div>

        <p className="mt-8 text-sm text-muted-foreground">
          Not sure a grant is needed? Read{" "}
          <Link
            to="/guides/$slug"
            params={{ slug: "do-i-need-probate-in-queensland" }}
            className="font-semibold text-primary underline"
          >
            do I need probate in Queensland
          </Link>
          , or see the{" "}
          <Link to="/probate-queensland" className="font-semibold text-primary underline">
            fixed-fee probate page
          </Link>
          . Can't locate the original will? Start with{" "}
          <Link to="/will-safe-custody" className="font-semibold text-primary underline">
            finding a will in safe custody
          </Link>
          .
        </p>
      </section>

      <CtaBand
        title="Rather talk it through first?"
        body={`Call ${FIRM.phone} and we'll tell you in five minutes whether the estate needs a grant.`}
      />
      <div className="h-20" />
    </>
  );
}
